import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { 
  Send, Paperclip, FileText, X, Loader2, MessageSquare, Building2, Plus, User, 
  Folder, Upload, Check, Plane, File, Shield, Eye, FileImage, FileCode, 
  Clock, CalendarIcon, Undo2, Activity, Phone, FilePlus, Edit3, Trash2, RefreshCw,
  Download, ExternalLink
} from "lucide-react";
import { format, isBefore, formatDistanceToNow, startOfDay } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { InlinePdfViewer } from "./InlinePdfViewer";

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  category: string;
}

interface SentEmailRecord {
  id: string;
  subject: string;
  body_html: string;
  body_text: string | null;
  sent_at: string;
  status: string;
  attachments: any;
  type: 'sent';
}

interface InboundEmailRecord {
  id: string;
  from_email: string;
  from_name: string | null;
  subject: string;
  body_text: string | null;
  body_html: string | null;
  received_at: string;
  attachments: any;
  type: 'received';
}

interface ScheduledEmailRecord {
  id: string;
  subject: string;
  body_html: string;
  body_text: string | null;
  scheduled_for: string;
  created_at: string;
  status: string;
  attachments: any;
  error_message?: string | null;
  sent_at?: string | null;
  type: 'scheduled';
}

interface ActivityLogRecord {
  id: string;
  action_type: string;
  action_description: string;
  created_at: string;
  metadata: any;
  type: 'activity';
}

type ThreadItem = SentEmailRecord | InboundEmailRecord | ScheduledEmailRecord | ActivityLogRecord;

interface Attachment {
  filename: string;
  contentType: string;
  // Legacy base64 (avoid persisting). May exist for older rows.
  content?: string;
  // Preferred: storage reference so previews work in-thread.
  storageBucket?: string;
  storagePath?: string;
  // Local-only preview URL for newly-uploaded files.
  localObjectUrl?: string;
}

type PreviewState =
  | { status: "idle" }
  | { status: "loading"; filename: string }
  | { status: "error"; filename: string; message: string }
  | { status: "ready"; filename: string; contentType: string; url: string; kind: "image" | "html" | "pdf" | "audio" | "video" | "office" | "text" | "download" };

interface InquiryFile {
  id: string;
  name: string;
  storage_path: string;
  type: 'contract' | 'itinerary' | 'attachment' | 'admin';
  status: string;
  created_at: string;
}

interface AdminFile {
  id: string;
  name: string;
  storage_path: string;
  created_at: string;
}

interface InquiryData {
  id: string;
  name: string;
  email: string;
  occasion?: string | null;
  event_dates_json?: any;
  total_cost?: number | null;
  packages_json?: any;
}

interface EmailComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquiry: InquiryData;
  contractPdf?: { filename: string; content: string } | null;
  onSuccess?: () => void;
}

export function EmailComposer({
  open,
  onOpenChange,
  inquiry,
  contractPdf,
  onSuccess,
}: EmailComposerProps) {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [threadItems, setThreadItems] = useState<ThreadItem[]>([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [inquiryFiles, setInquiryFiles] = useState<InquiryFile[]>([]);
  const [adminFiles, setAdminFiles] = useState<AdminFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingAdminFiles, setLoadingAdminFiles] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [attachingFiles, setAttachingFiles] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [previewThreadAttachment, setPreviewThreadAttachment] = useState<{filename: string; content?: string; url?: string; contentType: string} | null>(null);
  const [composePreview, setComposePreview] = useState<PreviewState>({ status: "idle" });
  const [threadPreview, setThreadPreview] = useState<PreviewState>({ status: "idle" });
  
  // Undo state
  const [lastSentEmailId, setLastSentEmailId] = useState<string | null>(null);
  const [lastSentResendId, setLastSentResendId] = useState<string | null>(null);
  const [undoTimeoutId, setUndoTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const [isUndoing, setIsUndoing] = useState(false);
  
  // Schedule state
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>();
  const [scheduleTime, setScheduleTime] = useState("09:00");
  
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const initialLoadDone = useRef(false);
  const shouldAutoScrollRef = useRef(true);
  
  // Countdown timer for undo
  useEffect(() => {
    if (undoCountdown > 0) {
      const interval = setInterval(() => {
        setUndoCountdown(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (undoCountdown === 0 && lastSentEmailId) {
      setLastSentEmailId(null);
      setLastSentResendId(null);
    }
  }, [undoCountdown, lastSentEmailId]);
  
  // Auto-scroll to bottom when thread items load
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || threadItems.length === 0) return;
    // Only autoscroll when the user is already near the bottom.
    if (shouldAutoScrollRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [threadItems]);

  const handleThreadScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const thresholdPx = 80;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx;
    shouldAutoScrollRef.current = nearBottom;
  };

  const buildEmailAttachmentPath = (inquiryId: string, filename: string) => {
    const safe = filename.replace(/[^a-zA-Z0-9._\-() ]+/g, "_");
    return `${inquiryId}/email-attachments/${Date.now()}-${safe}`;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const maxSize = 5 * 1024 * 1024; // 5MB limit per file
    const selected = Array.from(files);

    try {
      const uploaded = await Promise.all(
        selected.map(async (file) => {
          if (file.size > maxSize) {
            throw new Error(`${file.name} exceeds 5MB limit`);
          }

          const storageBucket = "contracts";
          const storagePath = buildEmailAttachmentPath(inquiry.id, file.name);

          // Upload to storage (so thread preview works later)
          const { error } = await supabase.storage.from(storageBucket).upload(storagePath, file, {
            contentType: file.type || "application/octet-stream",
            upsert: false,
          });

          if (error) throw error;

          return {
            filename: file.name,
            contentType: file.type || "application/octet-stream",
            storageBucket,
            storagePath,
            localObjectUrl: URL.createObjectURL(file),
          } satisfies Attachment;
        }),
      );

      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: any) {
      toast({
        title: "Attachment upload failed",
        description: err?.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fetchAdminFiles = useCallback(async () => {
    setLoadingAdminFiles(true);
    try {
      const { data, error } = await supabase.storage
        .from("contracts")
        .list("admin", { sortBy: { column: "created_at", order: "desc" } });

      if (error) {
        if (!error.message.includes("not found")) {
          throw error;
        }
        setAdminFiles([]);
        return;
      }

      const files: AdminFile[] = (data || [])
        .filter((f) => f.name !== ".emptyFolderPlaceholder")
        .map((f) => ({
          id: `admin-${f.id || f.name}`,
          name: f.name,
          storage_path: `admin/${f.name}`,
          created_at: f.created_at || new Date().toISOString(),
        }));

      setAdminFiles(files);
    } catch (error: any) {
      console.error("Error fetching admin files:", error);
    } finally {
      setLoadingAdminFiles(false);
    }
  }, []);

  const fetchInquiryFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const { data, error } = await supabase
        .from("inquiry_contracts")
        .select("id, contract_number, storage_path, status, created_at, event_details")
        .eq("inquiry_id", inquiry.id)
        .not("storage_path", "is", null);

      if (error) throw error;

      const files: InquiryFile[] = (data || []).map((c) => {
        const eventDetails = c.event_details as Record<string, unknown> | null;
        const isItinerary = c.contract_number?.startsWith('ITIN-') || 
          (eventDetails && eventDetails.type === 'travel_itinerary');
        const isUpload = c.contract_number?.startsWith('FILE-') ||
          (eventDetails && eventDetails.type === 'uploaded_file');
        return {
          id: c.id,
          name: c.contract_number || 'Document',
          storage_path: c.storage_path!,
          type: isUpload ? 'attachment' as const : isItinerary ? 'itinerary' as const : 'contract' as const,
          status: c.status,
          created_at: c.created_at,
        };
      });

      setInquiryFiles(files);
    } catch (error: any) {
      console.error("Error fetching inquiry files:", error);
    } finally {
      setLoadingFiles(false);
    }
  }, [inquiry.id]);

  const fetchAllFiles = useCallback(async () => {
    await Promise.all([fetchInquiryFiles(), fetchAdminFiles()]);
  }, [fetchInquiryFiles, fetchAdminFiles]);

  const getAdminFileName = (name: string) => {
    const dashIndex = name.indexOf('-');
    return dashIndex > 0 ? name.substring(dashIndex + 1) : name;
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const attachSelectedFiles = async () => {
    if (selectedFileIds.size === 0) return;

    setAttachingFiles(true);
    const newAttachments: Attachment[] = [];

    try {
      for (const fileId of selectedFileIds) {
        // Check if it's an admin file
        const isAdminFile = fileId.startsWith('admin-');
        const adminFile = isAdminFile ? adminFiles.find(f => f.id === fileId) : null;
        const inquiryFile = !isAdminFile ? inquiryFiles.find(f => f.id === fileId) : null;
        
        const storagePath = adminFile?.storage_path || inquiryFile?.storage_path;
        if (!storagePath) continue;

        // Determine content type from file extension or path
        let contentType = "application/octet-stream";
        if (storagePath.endsWith('.html')) contentType = "text/html";
        else if (storagePath.endsWith('.pdf')) contentType = "application/pdf";
        else if (storagePath.endsWith('.png')) contentType = "image/png";
        else if (storagePath.endsWith('.jpg') || storagePath.endsWith('.jpeg')) contentType = "image/jpeg";

        // Get original filename
        let filename: string;
        if (adminFile) {
          filename = getAdminFileName(adminFile.name);
        } else if (inquiryFile?.type === 'attachment') {
          const pathParts = storagePath.split('/');
          const lastPart = pathParts[pathParts.length - 1];
          const dashIndex = lastPart.indexOf('-');
          filename = dashIndex > 0 ? lastPart.substring(dashIndex + 1) : lastPart;
        } else {
          filename = `${inquiryFile?.name || 'document'}.html`;
        }

        newAttachments.push({
          filename,
          contentType,
          storageBucket: "contracts",
          storagePath,
        });
      }

      setAttachments(prev => [...prev, ...newAttachments]);
      setSelectedFileIds(new Set());
      setShowFilePicker(false);

      toast({
        title: "Files attached",
        description: `${newAttachments.length} file(s) added to email`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to attach files",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setAttachingFiles(false);
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'itinerary':
        return <Plane className="h-4 w-4 text-primary" />;
      case 'contract':
        return <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-primary" />;
      default:
        return <File className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getAttachmentIcon = (contentType: string, filename: string) => {
    if (contentType.startsWith('image/')) {
      return <FileImage className="h-4 w-4 text-primary" />;
    }
    if (contentType === 'application/pdf') {
      return <FileText className="h-4 w-4 text-destructive" />;
    }
    if (contentType === 'text/html') {
      return <FileCode className="h-4 w-4 text-primary" />;
    }
    if (filename.match(/\.(doc|docx|xls|xlsx|ppt|pptx)$/i)) {
      return <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    }
    return <File className="h-4 w-4 text-muted-foreground" />;
  };

  const canPreview = (contentType: string) => {
    return contentType.startsWith('image/') || contentType === 'text/html';
  };

  const openPreview = async (att: Attachment, mode: "compose" | "thread") => {
    const setState = mode === "compose" ? setComposePreview : setThreadPreview;
    setState({ status: "loading", filename: att.filename });

    try {
      const contentType = att.contentType || "application/octet-stream";
      const isImage = contentType.startsWith("image/");
      const isHtml = contentType === "text/html";
      const isPdf = contentType === "application/pdf";
      const isAudio = contentType.startsWith("audio/");
      const isVideo = contentType.startsWith("video/");
      const isOffice = [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation"
      ].includes(contentType);
      const isText = contentType.startsWith("text/") && !isHtml;

      // 1) Local object URL (fresh upload)
      if (att.localObjectUrl && (isImage || isHtml || isPdf || isAudio || isVideo)) {
        let kind: "image" | "html" | "pdf" | "audio" | "video" = "image";
        if (isHtml) kind = "html";
        else if (isPdf) kind = "pdf";
        else if (isAudio) kind = "audio";
        else if (isVideo) kind = "video";
        setState({ status: "ready", filename: att.filename, contentType, url: att.localObjectUrl, kind });
        return;
      }

      // 2) Legacy base64 content
      if (att.content) {
        if (isImage) {
          setState({ status: "ready", filename: att.filename, contentType, url: `data:${contentType};base64,${att.content}`, kind: "image" });
          return;
        }
        if (isHtml) {
          const html = atob(att.content);
          const blob = new Blob([html], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          setState({ status: "ready", filename: att.filename, contentType, url, kind: "html" });
          return;
        }
      }

      // 3) Storage reference - create signed URL
      if (att.storageBucket && att.storagePath) {
        const { data, error } = await supabase.storage
          .from(att.storageBucket)
          .createSignedUrl(att.storagePath, 60 * 10);
        if (error) throw error;
        
        // NOTE: Signed URLs for HTML are often blocked in iframes by browser security headers.
        // Workaround: fetch the HTML and preview via a local blob URL.
        if (isHtml) {
          const res = await fetch(data.signedUrl);
          if (!res.ok) throw new Error(`Failed to load HTML preview (${res.status})`);
          const html = await res.text();
          const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
          setState({ status: "ready", filename: att.filename, contentType, url: blobUrl, kind: "html" });
          return;
        }

        // Handle Office documents via Microsoft Office Viewer
        if (isOffice) {
          const encodedUrl = encodeURIComponent(data.signedUrl);
          const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
          setState({ status: "ready", filename: att.filename, contentType, url: officeViewerUrl, kind: "office" });
          return;
        }

        // Handle text files by fetching content
        if (isText) {
          const res = await fetch(data.signedUrl);
          if (!res.ok) throw new Error(`Failed to load text preview (${res.status})`);
          const text = await res.text();
          setState({ status: "ready", filename: att.filename, contentType, url: text, kind: "text" });
          return;
        }

        // Handle PDF files via blob URL to bypass iframe restrictions
        if (isPdf) {
          const res = await fetch(data.signedUrl);
          if (!res.ok) throw new Error(`Failed to load PDF preview (${res.status})`);
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          setState({ status: "ready", filename: att.filename, contentType, url: blobUrl, kind: "pdf" });
          return;
        }

        // Determine kind for other file types (images, audio, video)
        let kind: "image" | "audio" | "video" | "download" = "download";
        if (isImage) kind = "image";
        else if (isAudio) kind = "audio";
        else if (isVideo) kind = "video";
        
        setState({
          status: "ready",
          filename: att.filename,
          contentType,
          url: data.signedUrl,
          kind,
        });
        return;
      }

      setState({ status: "error", filename: att.filename, message: "Preview unavailable for this attachment." });
    } catch (err: any) {
      setState({ status: "error", filename: att.filename, message: err?.message || "Failed to load preview" });
    }
  };

  const renderPreview = (state: PreviewState) => {
    if (state.status === "idle") return null;
    if (state.status === "loading") {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (state.status === "error") {
      return (
        <div className="text-center py-10 text-muted-foreground">
          <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm">{state.message}</p>
        </div>
      );
    }

    if (state.kind === "image") {
      return (
        <div className="flex items-center justify-center bg-muted/50 rounded-lg p-4">
          <img src={state.url} alt={state.filename} className="max-w-full max-h-[400px] object-contain rounded shadow-sm" />
        </div>
      );
    }

    if (state.kind === "html") {
      return (
        <div className="border rounded-lg overflow-hidden bg-background">
          <iframe src={state.url} className="w-full h-[400px]" title="Document Preview" sandbox="" />
        </div>
      );
    }

    if (state.kind === "pdf") {
      return (
        <div className="border rounded-lg overflow-hidden bg-background h-[400px]">
          <InlinePdfViewer url={state.url} height="400px" />
        </div>
      );
    }

    if (state.kind === "audio") {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <div className="text-4xl">🎵</div>
          <p className="text-sm font-medium">{state.filename}</p>
          <audio controls className="w-full max-w-md" src={state.url}>
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    if (state.kind === "video") {
      return (
        <div className="flex items-center justify-center bg-background rounded-lg overflow-hidden">
          <video controls className="max-w-full max-h-[400px]" src={state.url}>
            Your browser does not support the video element.
          </video>
        </div>
      );
    }

    if (state.kind === "office") {
      return (
        <div className="border rounded-lg overflow-hidden bg-background">
          <iframe src={state.url} className="w-full h-[400px]" title="Office Document Preview" />
        </div>
      );
    }

    if (state.kind === "text") {
      return (
        <div className="border rounded-lg p-4 bg-muted/10 overflow-auto max-h-[400px]">
          <pre className="text-xs font-mono whitespace-pre-wrap break-words text-foreground">{state.url}</pre>
        </div>
      );
    }

    return (
      <div className="text-center py-8">
        <File className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-sm font-medium mb-2">{state.filename}</p>
        <Button asChild variant="outline">
          <a href={state.url} target="_blank" rel="noopener noreferrer">
            <Download className="h-4 w-4 mr-2" />
            Download
          </a>
        </Button>
      </div>
    );
  };

  useEffect(() => {
    if (open) {
      fetchData();
      // default: keep scroll anchored at bottom on initial open
      shouldAutoScrollRef.current = true;
      // Auto-attach contract if provided
      if (contractPdf) {
        setAttachments([{
          filename: contractPdf.filename,
          content: contractPdf.content,
          contentType: "application/pdf",
        }]);
      }
    }
  }, [open, contractPdf]);

  // Event-driven refresh (no polling) to avoid jitter.
  useEffect(() => {
    if (!open) return;

    const channel = supabase
      .channel(`email-thread-${inquiry.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "inquiry_emails", filter: `inquiry_id=eq.${inquiry.id}` },
        () => refreshThreadSilently(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "inquiry_inbound_emails", filter: `inquiry_id=eq.${inquiry.id}` },
        () => refreshThreadSilently(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "scheduled_emails", filter: `inquiry_id=eq.${inquiry.id}` },
        () => refreshThreadSilently(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "inquiry_activity_logs", filter: `inquiry_id=eq.${inquiry.id}` },
        () => refreshThreadSilently(),
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inquiry.id]);

  // Initial fetch with loading state
  const fetchData = async () => {
    setIsLoading(true);
    initialLoadDone.current = false;
    
    // Fetch templates, sent emails, inbound emails, scheduled emails, and activity logs in parallel
    const [templatesRes, sentRes, inboundRes, scheduledRes, activityRes] = await Promise.all([
      supabase
        .from("email_templates")
        .select("*")
        .eq("is_active", true)
        .order("category"),
      supabase
        .from("inquiry_emails")
        .select("*")
        .eq("inquiry_id", inquiry.id),
      supabase
        .from("inquiry_inbound_emails")
        .select("*")
        .eq("inquiry_id", inquiry.id),
      supabase
        .from("scheduled_emails")
        .select("*")
        .eq("inquiry_id", inquiry.id)
        .order("scheduled_for", { ascending: true }),
      supabase
        .from("inquiry_activity_logs")
        .select("*")
        .eq("inquiry_id", inquiry.id)
    ]);

    if (templatesRes.error) {
      console.error("Error fetching templates:", templatesRes.error);
    } else {
      setTemplates(templatesRes.data || []);
    }

    if (inboundRes.error) {
      console.error("Error fetching inbound emails:", inboundRes.error);
    }

    // Merge all items chronologically
    const sentEmails: ThreadItem[] = (sentRes.data || []).map(e => ({
      ...e,
      type: 'sent' as const,
    }));

    const inboundEmails: ThreadItem[] = (inboundRes.data || []).map(e => ({
      ...e,
      type: 'received' as const,
    }));

    const scheduledEmails: ThreadItem[] = (scheduledRes.data || []).map(e => ({
      ...e,
      type: 'scheduled' as const,
    }));

    const activityLogs: ThreadItem[] = (activityRes.data || []).map(e => ({
      ...e,
      type: 'activity' as const,
    }));

    const safeTime = (value?: string | null) => {
      const t = value ? new Date(value).getTime() : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    // One canonical timestamp per item type so sorting is always truly chronological.
    // IMPORTANT: For scheduled emails we sort by when the action happened:
    // - pending/cancelled/failed: created_at (when it was created in the system)
    // - sent: sent_at (when it actually delivered)
    const getThreadItemSortTime = (item: ThreadItem): number => {
      if (item.type === 'sent') return safeTime(item.sent_at);
      if (item.type === 'received') return safeTime(item.received_at);
      if (item.type === 'activity') return safeTime(item.created_at);
      // scheduled
      if (item.status === 'sent') return safeTime(item.sent_at) || safeTime(item.created_at) || safeTime(item.scheduled_for);
      return safeTime(item.created_at) || safeTime(item.scheduled_for);
    };

    const allItems = [...sentEmails, ...inboundEmails, ...scheduledEmails, ...activityLogs].sort((a, b) => {
      const ta = getThreadItemSortTime(a);
      const tb = getThreadItemSortTime(b);
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    });

    setThreadItems(allItems);
    setIsLoading(false);
    initialLoadDone.current = true;
    setLastRefreshedAt(new Date());
  };

  // Silent refresh - doesn't touch loading state, just updates thread
  const refreshThreadSilently = async () => {
    if (!initialLoadDone.current) return;
    
    const [sentRes, inboundRes, scheduledRes, activityRes] = await Promise.all([
      supabase
        .from("inquiry_emails")
        .select("*")
        .eq("inquiry_id", inquiry.id),
      supabase
        .from("inquiry_inbound_emails")
        .select("*")
        .eq("inquiry_id", inquiry.id),
      supabase
        .from("scheduled_emails")
        .select("*")
        .eq("inquiry_id", inquiry.id)
        .order("scheduled_for", { ascending: true }),
      supabase
        .from("inquiry_activity_logs")
        .select("*")
        .eq("inquiry_id", inquiry.id)
    ]);

    // Only update if we got data without errors
    if (sentRes.error || inboundRes.error || scheduledRes.error || activityRes.error) {
      console.error("Silent refresh errors:", { sentRes, inboundRes, scheduledRes, activityRes });
      return;
    }

    const sentEmails: ThreadItem[] = (sentRes.data || []).map(e => ({
      ...e,
      type: 'sent' as const,
    }));

    const inboundEmails: ThreadItem[] = (inboundRes.data || []).map(e => ({
      ...e,
      type: 'received' as const,
    }));

    const scheduledEmails: ThreadItem[] = (scheduledRes.data || []).map(e => ({
      ...e,
      type: 'scheduled' as const,
    }));

    const activityLogs: ThreadItem[] = (activityRes.data || []).map(e => ({
      ...e,
      type: 'activity' as const,
    }));

    const safeTime = (value?: string | null) => {
      const t = value ? new Date(value).getTime() : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    const getThreadItemSortTime = (item: ThreadItem): number => {
      if (item.type === 'sent') return safeTime(item.sent_at);
      if (item.type === 'received') return safeTime(item.received_at);
      if (item.type === 'activity') return safeTime(item.created_at);
      if (item.status === 'sent') return safeTime(item.sent_at) || safeTime(item.created_at) || safeTime(item.scheduled_for);
      return safeTime(item.created_at) || safeTime(item.scheduled_for);
    };

    const allItems = [...sentEmails, ...inboundEmails, ...scheduledEmails, ...activityLogs].sort((a, b) => {
      const ta = getThreadItemSortTime(a);
      const tb = getThreadItemSortTime(b);
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    });

    // Only update if there's a change (prevents unnecessary re-renders)
    setThreadItems(prev => {
      if (prev.length !== allItems.length) return allItems;
      // Quick check for any ID differences
      const prevIds = new Set(prev.map(p => p.id));
      const hasNew = allItems.some(a => !prevIds.has(a.id));
      if (hasNew) return allItems;
      // Check for status changes in scheduled emails
      const prevScheduled = prev.filter(p => p.type === 'scheduled') as ScheduledEmailRecord[];
      const newScheduled = allItems.filter(a => a.type === 'scheduled') as ScheduledEmailRecord[];
      const statusChanged = newScheduled.some(ns => {
        const ps = prevScheduled.find(p => p.id === ns.id);
        return ps && ps.status !== ns.status;
      });
      if (statusChanged) return allItems;
      return prev;
    });

    setLastRefreshedAt(new Date());
  };

  const replaceTemplatePlaceholders = (text: string): string => {
    const eventDate = inquiry.event_dates_json?.[0]?.date || "";
    const totalCost = inquiry.total_cost 
      ? `$${inquiry.total_cost.toLocaleString()}` 
      : "";

    return text
      .replace(/\{\{name\}\}/g, inquiry.name || "")
      .replace(/\{\{occasion\}\}/g, inquiry.occasion || "your event")
      .replace(/\{\{event_date\}\}/g, eventDate)
      .replace(/\{\{total_cost\}\}/g, totalCost)
      .replace(/\{\{email\}\}/g, inquiry.email || "");
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(replaceTemplatePlaceholders(template.subject));
      // If in plain text mode, strip HTML from template
      if (isHtmlMode) {
        setMessageBody(replaceTemplatePlaceholders(template.body_html));
      } else {
        const strippedText = template.body_html
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<\/p>/gi, "\n\n")
          .replace(/<[^>]*>/g, "")
          .trim();
        setMessageBody(replaceTemplatePlaceholders(strippedText));
      }
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (scheduled?: Date) => {
    if (!subject.trim() || !messageBody.trim()) {
      toast({
        title: "Missing fields",
        description: "Please fill in subject and message body",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Not authenticated");
      }

      // Convert plain text to simple HTML if not in HTML mode
      const bodyHtml = isHtmlMode 
        ? messageBody 
        : messageBody.split("\n").map(line => line.trim() ? `<p>${line}</p>` : "<br>").join("");
      
      const bodyText = isHtmlMode 
        ? messageBody.replace(/<[^>]*>/g, "") 
        : messageBody;

      const response = await supabase.functions.invoke("send-crm-email", {
        body: {
          inquiryId: inquiry.id,
          recipientEmail: inquiry.email,
          recipientName: inquiry.name,
          subject,
          bodyHtml,
          bodyText,
          templateUsed: selectedTemplate || null,
          attachments: attachments.length > 0 ? attachments : undefined,
          scheduledFor: scheduled?.toISOString() || null,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      const emailId = response.data?.emailId;
      const resendEmailId = response.data?.resendEmailId;

      if (scheduled) {
        toast({
          title: "Email scheduled! 📅",
          description: `Will be sent ${format(scheduled, "MMM d 'at' h:mm a")}`,
        });
        setShowSchedule(false);
        setScheduleDate(undefined);
        setScheduleTime("09:00");
      } else {
        // Enable undo for 10 seconds
        if (emailId) {
          setLastSentEmailId(emailId);
          setLastSentResendId(resendEmailId || null);
          setUndoCountdown(10);
          
          const timeoutId = setTimeout(() => {
            setLastSentEmailId(null);
            setLastSentResendId(null);
          }, 10000);
          setUndoTimeoutId(timeoutId);
        }
        
        toast({
          title: "Email sent! ✉️",
          description: `Email sent to ${inquiry.email}`,
        });
      }

      // Reset and refresh
      setSubject("");
      setMessageBody("");
      setAttachments([]);
      setSelectedTemplate("");
      
      // Refresh email history to show the new message
      fetchData();
      onSuccess?.();
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast({
        title: "Failed to send email",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };
  
  const handleUndo = async () => {
    if (!lastSentEmailId) return;
    
    setIsUndoing(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Not authenticated");
      }

      const { data, error: fnError } = await supabase.functions.invoke("cancel-email", {
        body: {
          emailId: lastSentEmailId,
          resendEmailId: lastSentResendId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (fnError) throw fnError;
      
      // Clear undo state
      if (undoTimeoutId) clearTimeout(undoTimeoutId);
      setLastSentEmailId(null);
      setLastSentResendId(null);
      setUndoCountdown(0);
      
      const wasRecalled = data?.cancelled === "delivery";
      const wasCancelledInDB = data?.cancelled === "marked_cancelled";
      
      toast({
        title: wasRecalled ? "Email recalled" : wasCancelledInDB ? "Email cancelled" : "Recall not possible",
        description: wasRecalled 
          ? "Delivery was cancelled in time"
          : wasCancelledInDB
          ? "Email marked as cancelled and will remain in history"
          : data?.message || "Email was already delivered; it remains in conversation history",
      });
      
      fetchData();
    } catch (error: any) {
      console.error("Error undoing send:", error);
      toast({
        title: "Failed to undo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUndoing(false);
    }
  };
  
  const handleScheduleSend = () => {
    if (!scheduleDate || !scheduleTime) {
      toast({
        title: "Select date and time",
        description: "Please pick when to send the email",
        variant: "destructive",
      });
      return;
    }
    
    const [hours, minutes] = scheduleTime.split(":").map(Number);
    const scheduledDateTime = new Date(scheduleDate);
    scheduledDateTime.setHours(hours, minutes, 0, 0);
    
    if (isBefore(scheduledDateTime, new Date())) {
      toast({
        title: "Invalid time",
        description: "Scheduled time must be in the future",
        variant: "destructive",
      });
      return;
    }
    
    handleSend(scheduledDateTime);
  };

  const stripHtml = (html: string) => {
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .trim();
  };

  // Activity icon helper
  const getActivityIcon = (actionType: string) => {
    switch (actionType) {
      case 'status_changed':
        return <RefreshCw className="h-3 w-3 text-primary" />;
      case 'note_added':
        return <Edit3 className="h-3 w-3 text-primary" />;
      case 'call_scheduled':
      case 'call_rescheduled':
      case 'call_cancelled':
        return <Phone className="h-3 w-3 text-primary" />;
      case 'document_created':
      case 'document_edited':
      case 'document_previewed':
      case 'document_sent':
      case 'document_signed':
      case 'document_deleted':
        return <FilePlus className="h-3 w-3 text-primary" />;
      case 'email_sent':
        return <Send className="h-3 w-3 text-primary" />;
      default:
        return <Activity className="h-3 w-3 text-muted-foreground" />;
    }
  };

  // Cancel scheduled email
  const handleCancelScheduled = async (scheduledEmailId: string) => {
    try {
      const { error } = await supabase
        .from("scheduled_emails")
        .update({ status: "cancelled" })
        .eq("id", scheduledEmailId);

      if (error) throw error;

      toast({
        title: "Email cancelled",
        description: "The scheduled email has been cancelled",
      });

      fetchData();
    } catch (error: any) {
      toast({
        title: "Failed to cancel",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Edit scheduled email - populate composer with its content
  const handleEditScheduled = async (scheduledEmail: ScheduledEmailRecord) => {
    setSubject(scheduledEmail.subject);
    setMessageBody(stripHtml(scheduledEmail.body_html));
    
    // Cancel the old scheduled email
    await handleCancelScheduled(scheduledEmail.id);

    toast({
      title: "Editing scheduled email",
      description: "Make your changes and send or reschedule",
    });
  };

  // Preview thread attachment
  const handlePreviewThreadAttachment = (att: any) => {
    const contentType = att.contentType || att.content_type || 'application/octet-stream';
    setPreviewThreadAttachment({
      filename: att.filename,
      content: att.content,
      url: att.url,
      contentType,
    });
    // Trigger preview loading with new helper
    openPreview(
      {
        filename: att.filename,
        contentType,
        content: att.content,
        storageBucket: att.storageBucket || att.storage_bucket,
        storagePath: att.storagePath || att.storage_path,
      },
      "thread",
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-muted/30">
          <DialogTitle className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              <div>
                <span>Email Conversation</span>
                <p className="text-sm font-normal text-muted-foreground mt-0.5">
                  {inquiry.name} • {inquiry.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {lastRefreshedAt && (
                <span className="text-xs font-normal text-muted-foreground hidden sm:inline">
                  Updated {formatDistanceToNow(lastRefreshedAt, { addSuffix: true })}
                </span>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => refreshThreadSilently()}
                title="Refresh thread"
                className="h-8 w-8 p-0"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Chat-like Thread - Scrollable */}
          <div 
            ref={scrollContainerRef}
            onScroll={handleThreadScroll}
            className="flex-1 px-6 py-4 bg-muted/10 overflow-y-auto"
            style={{ maxHeight: '300px' }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : threadItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No messages yet</p>
                <p className="text-xs mt-1">Start the conversation below</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {threadItems.filter((t) => t.type === 'received').length === 0 && (
                  <div className="rounded border bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground flex items-center justify-between gap-2">
                    <span>No inbound replies yet</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => refreshThreadSilently()} className="h-6 px-2 text-xs">
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Check
                    </Button>
                  </div>
                )}
                {threadItems.map((item) => {
                  // Activity Log - inline pill
                  if (item.type === 'activity') {
                    return (
                      <div key={item.id} className="flex items-center gap-1 py-0.5">
                        <div className="flex-1 border-t border-dashed border-muted-foreground/20" />
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/40 text-[11px] text-muted-foreground">
                          {getActivityIcon(item.action_type)}
                          <span className="truncate max-w-[180px]">{item.action_description}</span>
                          <span className="text-muted-foreground/60 shrink-0">
                            {format(new Date(item.created_at), "M/d h:mm a")}
                          </span>
                        </div>
                        <div className="flex-1 border-t border-dashed border-muted-foreground/20" />
                      </div>
                    );
                  }

                  // Scheduled Email - compact card
                  if (item.type === 'scheduled') {
                    const isPending = item.status === 'pending';
                    const isSent = item.status === 'sent';
                    const isCancelled = item.status === 'cancelled';
                    const isFailed = item.status === 'failed';
                    const eventTs = isSent ? (item.sent_at || item.created_at || item.scheduled_for) : (item.created_at || item.scheduled_for);

                    return (
                      <div
                        key={item.id}
                        className={`flex items-start gap-2 p-2 rounded border text-xs ${
                          isCancelled ? 'opacity-50 bg-muted/30 border-muted' :
                          isSent ? 'bg-green-500/5 border-green-500/20' :
                          isFailed ? 'bg-destructive/5 border-destructive/20' :
                          'bg-primary/5 border-dashed border-primary/30'
                        }`}
                      >
                        <Clock className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${
                          isSent ? 'text-green-600' : isCancelled ? 'text-muted-foreground' : isFailed ? 'text-destructive' : 'text-primary'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant={isSent ? 'default' : isFailed ? 'destructive' : 'outline'} className="text-[10px] h-4 px-1">
                              {item.status}
                            </Badge>
                            <span className="font-medium truncate">{item.subject}</span>
                            <span className="text-muted-foreground/70 ml-auto shrink-0">
                              {format(new Date(eventTs), "M/d h:mm a")}
                            </span>
                          </div>
                          {isPending && (
                            <div className="flex gap-1 mt-1">
                              <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => handleCancelScheduled(item.id)}>
                                <X className="h-2.5 w-2.5 mr-0.5" />Cancel
                              </Button>
                              <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => handleEditScheduled(item)}>
                                <Edit3 className="h-2.5 w-2.5 mr-0.5" />Edit
                              </Button>
                            </div>
                          )}
                          {isFailed && item.error_message && (
                            <p className="text-destructive mt-0.5 truncate">{item.error_message}</p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Sent / Received Email - compact bubble
                  const isSent = item.type === 'sent';
                  const timestamp = isSent ? item.sent_at : item.received_at;
                  const bodyText = item.body_text || (item.body_html ? stripHtml(item.body_html) : '');
                  const isLong = bodyText.length > 120;
                  const preview = isLong ? bodyText.slice(0, 120) + '…' : bodyText;
                  const hasAttachments = item.attachments && Array.isArray(item.attachments) && item.attachments.length > 0;

                  return (
                    <div
                      key={item.id}
                      className={`group flex items-start gap-2 ${!isSent ? 'flex-row-reverse' : ''}`}
                    >
                      <div className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center ${isSent ? 'bg-primary/10' : 'bg-accent'}`}>
                        {isSent ? <Building2 className="h-3 w-3 text-primary" /> : <User className="h-3 w-3 text-accent-foreground" />}
                      </div>
                      <div className={`flex-1 min-w-0 ${!isSent ? 'text-right' : ''}`}>
                        <div className={`inline-block max-w-full text-left rounded-lg px-2.5 py-1.5 text-xs ${isSent ? 'bg-primary/5' : 'bg-accent/50'}`}>
                          <div className="flex items-center gap-1.5 mb-0.5 text-[10px] text-muted-foreground">
                            <span className="font-medium text-foreground">{item.subject}</span>
                            <span className="ml-auto shrink-0">{format(new Date(timestamp), "M/d h:mm a")}</span>
                          </div>
                          <p className="whitespace-pre-wrap break-words leading-snug">{preview}</p>
                          {hasAttachments && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.attachments.map((att: any, idx: number) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-0.5 bg-background/60 border rounded px-1 py-0.5 text-[10px] cursor-pointer hover:border-primary/50"
                                  onClick={() => handlePreviewThreadAttachment(att)}
                                >
                                  <Paperclip className="h-2.5 w-2.5" />
                                  <span className="max-w-[80px] truncate">{att.filename}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Undo Banner */}
          {lastSentEmailId && undoCountdown > 0 && (
            <div className="mx-6 my-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between">
              <span className="text-sm text-amber-600 dark:text-amber-400">
                Email sent • Recall available for {undoCountdown}s
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleUndo}
                disabled={isUndoing}
                className="h-7 text-amber-600 dark:text-amber-400 hover:text-amber-700 hover:bg-amber-500/20"
              >
                {isUndoing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Undo2 className="h-4 w-4 mr-1" />
                )}
                {isUndoing ? "Recalling..." : "Undo"}
              </Button>
            </div>
          )}

          {/* Compose Area */}
          <div className="border-t bg-muted/30 px-6 py-4 space-y-4">
            {/* Template and Mode Row */}
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Use template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {template.category}
                          </Badge>
                          {template.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="html-mode" className="text-xs text-muted-foreground">HTML</Label>
                <Switch
                  id="html-mode"
                  checked={isHtmlMode}
                  onCheckedChange={setIsHtmlMode}
                />
              </div>
            </div>

            {/* Subject */}
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject..."
              className="h-9"
            />

            {/* Message Body */}
            <div className="relative">
              <Textarea
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                placeholder={isHtmlMode ? "Write your message (HTML supported)..." : "Write your message..."}
                rows={4}
                className="pr-24 resize-none"
              />
              <div className="absolute bottom-2 right-2 flex items-center gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowFilePicker(true);
                    fetchAllFiles();
                  }}
                  title="Attach files"
                >
                  <Plus className="h-4 w-4" />
                  <Paperclip className="h-4 w-4" />
                </Button>
                {attachments.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {attachments.length}
                  </Badge>
                )}
                
                {/* Schedule Popover */}
                <Popover open={showSchedule} onOpenChange={setShowSchedule}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={isSending || !subject.trim() || !messageBody.trim()}
                    >
                      <Clock className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-4" align="end">
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Schedule send</p>
                      <Calendar
                        mode="single"
                        selected={scheduleDate}
                        onSelect={setScheduleDate}
                        // Allow selecting today; block only dates strictly before today.
                        disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))}
                        className="rounded-md border pointer-events-auto"
                      />
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <Input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowSchedule(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={handleScheduleSend}
                          disabled={!scheduleDate}
                          className="flex-1"
                        >
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Schedule
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                
                {/* Send Now */}
                <Button 
                  onClick={() => handleSend()} 
                  disabled={isSending || !subject.trim() || !messageBody.trim()}
                  size="sm"
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Attachments Row */}
            {attachments.length > 0 && (
              <Card className="bg-muted/30 border-dashed">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">
                      {attachments.length} Attachment{attachments.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((att, idx) => (
                      <div 
                        key={idx} 
                        className="group flex items-center gap-2 bg-background border rounded-lg px-3 py-2 hover:border-primary/50 transition-colors"
                      >
                        {getAttachmentIcon(att.contentType, att.filename)}
                        <span className="text-sm font-medium max-w-[150px] truncate">{att.filename}</span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canPreview(att.contentType) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                setPreviewAttachment(att);
                                openPreview(att, "compose");
                              }}
                              title="Preview"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-destructive/20 hover:text-destructive"
                            onClick={() => removeAttachment(idx)}
                            title="Remove"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {isHtmlMode && (
              <p className="text-xs text-muted-foreground">
                HTML mode enabled. Use tags like &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a href=""&gt;
              </p>
            )}
          </div>
        </div>

        {/* File Picker Dialog */}
        <Dialog open={showFilePicker} onOpenChange={setShowFilePicker}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Paperclip className="h-5 w-5 text-primary" />
                Attach Files
              </DialogTitle>
              <DialogDescription>
                Select files from this inquiry, admin folder, or upload new ones
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="files" className="w-full">
              <TabsList className="grid w-full grid-cols-3 h-11">
                <TabsTrigger value="files" className="gap-2 data-[state=active]:bg-primary/10">
                  <Folder className="h-4 w-4" />
                  Inquiry
                </TabsTrigger>
                <TabsTrigger value="admin" className="gap-2 data-[state=active]:bg-primary/10">
                  <Shield className="h-4 w-4" />
                  Admin
                </TabsTrigger>
                <TabsTrigger value="upload" className="gap-2 data-[state=active]:bg-primary/10">
                  <Upload className="h-4 w-4" />
                  Upload
                </TabsTrigger>
              </TabsList>

              <TabsContent value="files" className="mt-4">
                {loadingFiles ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : inquiryFiles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Folder className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No files for this inquiry</p>
                    <p className="text-xs mt-1">Upload files or create contracts first</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[280px] pr-4">
                    <div className="space-y-2">
                      {inquiryFiles.map((file) => {
                        const isSelected = selectedFileIds.has(file.id);
                        return (
                          <div
                            key={file.id}
                            onClick={() => toggleFileSelection(file.id)}
                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border-2 ${
                              isSelected 
                                ? 'bg-primary/10 border-primary shadow-sm' 
                                : 'bg-card border-border hover:border-primary/30 hover:bg-muted/50'
                            }`}
                          >
                            <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30 bg-background'
                            }`}>
                              {isSelected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                            </div>
                            <div className="h-10 w-10 rounded-lg bg-muted/50 flex items-center justify-center">
                              {getFileIcon(file.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{file.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(file.created_at), "MMM d, yyyy")}
                              </p>
                            </div>
                            <Badge variant={isSelected ? "default" : "outline"} className="text-[10px]">
                              {file.type}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>

              <TabsContent value="admin" className="mt-4">
                {loadingAdminFiles ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : adminFiles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No admin files available</p>
                    <p className="text-xs mt-1">Add files to the Admin folder in Files Manager</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[280px] pr-4">
                    <div className="space-y-2">
                      {adminFiles.map((file) => {
                        const isSelected = selectedFileIds.has(file.id);
                        return (
                          <div
                            key={file.id}
                            onClick={() => toggleFileSelection(file.id)}
                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border-2 ${
                              isSelected 
                                ? 'bg-primary/10 border-primary shadow-sm' 
                                : 'bg-card border-border hover:border-primary/30 hover:bg-muted/50'
                            }`}
                          >
                            <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30 bg-background'
                            }`}>
                              {isSelected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                            </div>
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Shield className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{getAdminFileName(file.name)}</p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(file.created_at), "MMM d, yyyy")}
                              </p>
                            </div>
                            <Badge variant={isSelected ? "default" : "outline"} className="text-[10px] border-primary/50">
                              admin
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>

              <TabsContent value="upload" className="mt-4">
                <div 
                  className="border-2 border-dashed rounded-xl p-10 text-center hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/10 transition-colors">
                    <Upload className="h-7 w-7 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-sm font-medium">Click to upload files</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    PDF, DOC, DOCX, TXT, PNG, JPG, GIF (max 5MB)
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            {selectedFileIds.size > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="rounded-full">
                    {selectedFileIds.size}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    file{selectedFileIds.size > 1 ? 's' : ''} selected
                  </span>
                </div>
                <Button onClick={attachSelectedFiles} disabled={attachingFiles} className="gap-2">
                  {attachingFiles ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                  Attach Files
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Compose Attachment Preview Dialog */}
        <Dialog open={!!previewAttachment} onOpenChange={() => setPreviewAttachment(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                Preview: {previewAttachment?.filename}
              </DialogTitle>
              <DialogDescription>
                Attachment preview
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              {renderPreview(composePreview)}
            </ScrollArea>
            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setPreviewAttachment(null)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Thread Attachment Preview Dialog */}
        <Dialog open={!!previewThreadAttachment} onOpenChange={() => setPreviewThreadAttachment(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                {previewThreadAttachment?.filename}
              </DialogTitle>
              <DialogDescription>
                Attachment from conversation
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              {renderPreview(threadPreview)}
            </ScrollArea>
            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setPreviewThreadAttachment(null)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
